import { Application, Feature } from '@frontend/functionality/types';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { ModuleClient } from '@frontend/module/api';
import { Module } from '@frontend/module/types';
import { SlotClient, SlotWorkflowClient } from '@frontend/slot/api';
import { Slot, SlotRendering } from '@frontend/slot/types';
import React, { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { SlotLayoutCell, slotLayoutDisplayNumber } from '../../components/slot-layout-cell';
import { useAppSelector } from '../../redux/store';
import { useKioskNavigate } from '../../utils/kiosk-navigation';
import { useLicenseRepository } from '../../utils/license-repository';
import { assignAllUnitsToDestinationSlot, expandTransactionsToColliUnits } from './bulk-move-colli-units';
import { BulkMoveLocationState } from './bulk-move-location-state';

export interface BulkMoveAssignSlotsViewProps {
    accountId: string | undefined;
    spotId: string | undefined;
    transactionCount: number;
    availableSlotCount: number;
    currentModule: Module | null;
    modules: Module[] | null;
    selectedSlot: Slot | null;
    setSelectedSlot: (slot: Slot | null) => void;
    setCurrentModule: (module: Module | null) => void;
    slotComponent: (slotRendering: SlotRendering) => React.ReactNode;
    selectModuleModalOpen: boolean;
    setSelectModuleModalOpen: (open: boolean) => void;
    isLoading: boolean;
    error: ReactNode | null;
    canContinue: boolean;
    handleContinue: () => void;
    handleBack: () => void;
}

export const useBulkMoveAssignSlots = (): BulkMoveAssignSlotsViewProps => {
    const { destSpotId } = useParams<{ destSpotId: string }>();
    const navigate = useKioskNavigate();
    const location = useLocation();
    const navigationState = useAppSelector((state) => state.navigation);
    const accountId = navigationState.user?.accountId ?? navigationState.iot?.account_id;
    const { getFunctionality } = useLicenseRepository();
    const bulkFunctionality = getFunctionality(Application.PUDO, Feature.BULK_MOVE_TRANSACTION);

    const transactions = (location.state as BulkMoveLocationState | null)?.transactions ?? [];
    const units = useMemo(() => expandTransactionsToColliUnits(transactions), [transactions]);

    const [modules, setModules] = useState<Module[] | null>(null);
    const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
    const [allSlots, setAllSlots] = useState<Slot[] | null>(null);
    const [availableSlotIds, setAvailableSlotIds] = useState<Set<string>>(new Set());
    const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);
    const [selectModuleModalOpen, setSelectModuleModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<ReactNode | null>(null);

    useEffect(() => {
        if (!destSpotId || !accountId || transactions.length === 0 || units.length === 0) {
            setIsLoading(false);
            setError(CommonMessage.ERRORS.DEFAULT);
            return;
        }

        setIsLoading(true);
        setError(null);
        setSelectedSlot(null);

        Promise.all([
            ModuleClient.fetchModules({ spot_id: destSpotId }),
            SlotClient.fetchSlots({ spot_id: destSpotId, size: '1000', index: '0' }),
            SlotWorkflowClient.postAvailableSlots(accountId, destSpotId, {
                account_id: accountId,
                application: Application.PUDO,
                feature: Feature.BULK_MOVE_TRANSACTION,
                functionality_id: bulkFunctionality?.id,
                transaction_count: transactions.length
            })
        ])
            .then(([modulesRes, slotsRes, availableRes]) => {
                const sorted = (modulesRes.results ?? []).sort((a, b) => {
                    const indexA = a.index || '';
                    const indexB = b.index || '';
                    return indexA.localeCompare(indexB, undefined, { numeric: true, sensitivity: 'base' });
                });
                setModules(sorted);
                setAllSlots(slotsRes.results ?? []);
                setCurrentModuleIndex(0);

                const available = availableRes.results ?? [];
                setAvailableSlotIds(new Set(available.map((slot) => slot.id)));
            })
            .catch((err) => {
                Logger.error('Failed to load bulk move destination SLOTs', err);
                setModules(null);
                setAllSlots(null);
                setAvailableSlotIds(new Set());
                setError(CommonMessage.ERRORS.FAILED_TO_LOAD);
            })
            .finally(() => setIsLoading(false));
    }, [accountId, bulkFunctionality?.id, destSpotId, transactions.length, units.length]);

    const currentModule = modules && modules.length > 0 ? modules[currentModuleIndex] : null;

    const setCurrentModule = (module: Module | null) => {
        if (modules && module) {
            const index = modules.findIndex((m) => m.id === module.id);
            if (index >= 0) setCurrentModuleIndex(index);
        }
        setSelectModuleModalOpen(false);
    };

    const handleSlotClick = useCallback(
        (slot: Slot) => {
            if (!availableSlotIds.has(slot.id)) return;
            setSelectedSlot(slot);
        },
        [availableSlotIds]
    );

    const slotComponent = useCallback(
        (slotRendering: SlotRendering): React.ReactNode => {
            const renderedSlotId = slotRendering.slot_ids.find((id) => allSlots?.some((s) => s.id === id));
            const slot = allSlots?.find((s) => s.id === renderedSlotId) ?? null;
            const isSelected = selectedSlot != null && !!slot && slotRendering.slot_ids.includes(selectedSlot.id);
            const slotNumber = slotLayoutDisplayNumber(slot);
            const hasTransaction = (slotRendering.transactions?.length ?? 0) > 0;
            const hasContent = slotRendering.occupied === true || (slotRendering.stocks?.length ?? 0) > 0 || hasTransaction;
            const isSelectable = !!slot && availableSlotIds.has(slot.id);

            return (
                <SlotLayoutCell
                    slotNumber={slotNumber}
                    isSelected={isSelected}
                    isDisabled={!isSelectable}
                    hasContent={hasContent}
                    onSelect={isSelectable && slot ? () => handleSlotClick(slot) : undefined}
                />
            );
        },
        [allSlots, availableSlotIds, handleSlotClick, selectedSlot]
    );

    const handleContinue = useCallback(() => {
        if (!destSpotId || !selectedSlot || units.length === 0) return;
        const assignments = assignAllUnitsToDestinationSlot(units, selectedSlot);
        navigate(`/spots/layout/pudo/bulk-move-transactions/move/spot/${destSpotId}/confirm`, {
            state: { transactions, assignments } satisfies BulkMoveLocationState
        });
    }, [destSpotId, navigate, selectedSlot, transactions, units]);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return {
        accountId,
        spotId: destSpotId,
        transactionCount: transactions.length,
        availableSlotCount: availableSlotIds.size,
        currentModule,
        modules,
        selectedSlot,
        setSelectedSlot,
        setCurrentModule,
        slotComponent,
        selectModuleModalOpen,
        setSelectModuleModalOpen,
        isLoading,
        error,
        canContinue: !!selectedSlot && availableSlotIds.has(selectedSlot.id) && units.length > 0 && !isLoading && !error,
        handleContinue,
        handleBack
    };
};
